// @givmo/board — the four dialogs a compensation decision is entered and changed through (CBG-YR4-PAY,
// CBG-YR4-PAY-CONSENT; the backend's `board-compensation-decisions.md` §3, §5, §7.1):
//
//   * `MotionDecisionDialog` — "Compensation details" on a carried motion (frame c1): whose pay, the
//     organization's Form 990 answer, the meeting's own papers the board relied on (ticked in the
//     order the board names them), how it obtained them and its reason when it set pay outside that
//     range. Added (POST) or replaced whole (PUT: an optional field left empty is cleared).
//   * `ConsentDecisionDialog` — the same on a written consent's draft, less the papers: a consent
//     decision's documents are uploaded onto it, on the consent's own panel.
//   * `IndexEntryDialog` — "Add a decision from your records": a label on minutes, a resolution or a
//     written consent already in the minute book, which never changes the record.
//   * `AnswerDialog` — "Change your Form 990 answer", the one change a decision takes once its
//     details stand (the minutes approved, the consent gone out): the label's PATCH, which can also
//     take the answer back to none ("Neither line 15a nor 15b").
//
// Every refusal is the backend's own sentence, said in the dialog, which stays open: the record
// closed meanwhile, the motion not carried, a paper changed while Board was taking its fingerprint,
// one over the cap. Nothing here is computed or judged: Board records what the officer enters.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCompensationDecisionOut,
  type BoardCompensationPaperOut,
  type BoardMotionOut,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Checkbox,
  ErrorMessage,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import { DialogFrame, PartLabel } from '../conflicts/coi-parts';
import { extractRecords, recordKindLabel } from '../records/records-decode';
import { form990LineOf, type Form990Line } from './compensation-decode';
import { Form990Answer, WhosePayFields } from './compensation-fields';
import { compensationWords as w } from './compensation-words';

const SMALL = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const QUOTE =
  'whitespace-pre-wrap border-l-2 border-border-strong pl-12 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong';
const DETAILS_MAX = 2000;

function failure(caught: unknown): string {
  return isGivmoError(caught) ? caught.message : w.saveFailed;
}

/** The two closing lines every dialog of the record's own details ends with (frame c1). */
function ClosingLines({ prints }: { prints: string }) {
  return (
    <div className="flex flex-col gap-6">
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {prints}
      </p>
      <p className={SMALL}>{w.provenance}</p>
    </div>
  );
}

/**
 * How the board obtained what it relied on, and its reason when it set pay outside that range — a
 * committee's decision, the committee's (CBG-YR6).
 */
function TheBoardsWords({
  committee,
  howObtained,
  reason,
  onHowObtained,
  onReason,
  testId,
}: {
  committee: boolean;
  howObtained: string;
  reason: string;
  onHowObtained: (value: string) => void;
  onReason: (value: string) => void;
  testId: string;
}) {
  return (
    <>
      <Textarea
        label={committee ? w.committeeObtainedLabel : w.obtainedLabel}
        rows={3}
        maxLength={DETAILS_MAX}
        value={howObtained}
        onChange={(event) => onHowObtained(event.target.value)}
        testId={`${testId}-obtained`}
      />
      <Textarea
        label={committee ? w.committeeOutsideLabel : w.outsideLabel}
        helper={w.optional}
        rows={2}
        maxLength={DETAILS_MAX}
        value={reason}
        onChange={(event) => onReason(event.target.value)}
        testId={`${testId}-outside`}
      />
    </>
  );
}

function SaveButtons({
  onCancel,
  onSave,
  saving,
  disabled,
  label = w.save,
  testId,
}: {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  disabled: boolean;
  label?: string;
  testId: string;
}) {
  return (
    <>
      <Button
        appearance="saas"
        tone="secondary"
        disabled={saving}
        onClick={onCancel}
        testId={`${testId}-cancel`}
      >
        {w.cancel}
      </Button>
      <Button
        appearance="saas"
        tone="primary"
        loading={saving}
        disabled={disabled}
        onClick={onSave}
        testId={`${testId}-save`}
      >
        {label}
      </Button>
    </>
  );
}

/** The fields a decision is entered with, held as the dialog edits them. */
function useDecisionFields(decision: BoardCompensationDecisionOut | null) {
  const [name, setName] = useState(decision?.person_name ?? '');
  const [position, setPosition] = useState(decision?.position ?? '');
  const [line, setLine] = useState<Form990Line | null>(form990LineOf(decision?.form_990_line));
  const [howObtained, setHowObtained] = useState(decision?.relied_on?.how_obtained ?? '');
  const [reason, setReason] = useState(decision?.relied_on?.reason_outside_range ?? '');
  const complete = name.trim().length > 0 && position.trim().length > 0;
  return {
    name,
    position,
    line,
    howObtained,
    reason,
    setName,
    setPosition,
    setLine,
    setHowObtained,
    setReason,
    complete,
  };
}

// ── On a carried motion (frame c1) ─────────────────────────────────────────────────────────────

export function MotionDecisionDialog({
  orgRef,
  meetingId,
  committee = false,
  motion,
  meetingDay,
  papers,
  decision,
  answersForm990,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  /** CBG-YR6: a committee meeting's motion, whose decision is the committee's. */
  committee?: boolean;
  motion: BoardMotionOut;
  /** The meeting's own day, "Oct 14, 2026", for "Carried · …"; null where it can't be read. */
  meetingDay: string | null;
  papers: readonly BoardCompensationPaperOut[];
  /** The decision being changed, or null for a new one. */
  decision: BoardCompensationDecisionOut | null;
  answersForm990: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const fields = useDecisionFields(decision);
  // The papers named, in the order the board names them: kept in the order they were ticked.
  const [chosen, setChosen] = useState<string[]>(
    (decision?.relied_on?.documents ?? [])
      .map((document) => document.packet_document_id)
      .filter((id): id is string => typeof id === 'string'),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      // A replace sets the whole decision (an omitted field is cleared), so the seat link, a label
      // this dialog doesn't edit, goes back as it stands (review-187's F4).
      const body = {
        person_name: fields.name.trim(),
        position: fields.position.trim(),
        board_member_id: decision?.board_member_id ?? null,
        form_990_line: answersForm990 ? fields.line : null,
        document_ids: chosen,
        how_obtained: fields.howObtained.trim() || null,
        reason_outside_range: fields.reason.trim() || null,
      };
      const path = { org_ref: orgRef, meeting_id: meetingId, motion_id: motion.id };
      if (decision === null) {
        await operations.officerAddBoardMotionCompensationDecision({
          path,
          body,
          throwOnError: true,
        });
      } else {
        await operations.officerReplaceBoardMotionCompensationDecision({
          path: { ...path, decision_id: decision.id },
          body,
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(w.saved);
      onSaved();
    },
    onError: (caught) => setError(failure(caught)),
  });

  const toggle = (paperId: string, checked: boolean) =>
    setChosen((current) =>
      checked
        ? current.includes(paperId)
          ? current
          : [...current, paperId]
        : current.filter((id) => id !== paperId),
    );

  return (
    <DialogFrame
      title={w.title}
      width={600}
      onClose={onClose}
      testId="compensation-dialog"
      footer={
        <SaveButtons
          onCancel={onClose}
          onSave={() => {
            setError(null);
            save.mutate();
          }}
          saving={save.isPending}
          disabled={!fields.complete}
          testId="compensation"
        />
      }
    >
      <div className="flex flex-col gap-4">
        <blockquote className={QUOTE} data-testid="compensation-motion">
          {motion.motion_text}
        </blockquote>
        {meetingDay !== null ? (
          <span className={`${SMALL} pl-14`}>{w.carriedOn(meetingDay)}</span>
        ) : null}
      </div>

      <WhosePayFields
        name={fields.name}
        position={fields.position}
        line={fields.line}
        answersForm990={answersForm990}
        onName={fields.setName}
        onPosition={fields.setPosition}
        onLine={fields.setLine}
        testId="compensation"
      />

      <div className="flex flex-col gap-10">
        <PartLabel>{committee ? w.committeeReliedOnLabel : w.reliedOnLabel}</PartLabel>
        {papers.length > 0 ? (
          <>
            <span className={SMALL}>{w.fromPapers}</span>
            <ul className="flex flex-col gap-8" data-testid="compensation-papers">
              {papers.map((paper) => (
                <li key={paper.id}>
                  <label className="flex cursor-pointer items-start gap-10">
                    <Checkbox
                      appearance="saas"
                      checked={chosen.includes(paper.id)}
                      onCheckedChange={(checked) => toggle(paper.id, checked)}
                      testId={`compensation-paper-${paper.id}`}
                    />
                    <span className="min-w-0 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong [overflow-wrap:anywhere]">
                      {paper.file_name}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <span className={SMALL} data-testid="compensation-no-papers">
            {w.noPapers}
          </span>
        )}
        <TheBoardsWords
          committee={committee}
          howObtained={fields.howObtained}
          reason={fields.reason}
          onHowObtained={fields.setHowObtained}
          onReason={fields.setReason}
          testId="compensation"
        />
      </div>

      {error !== null ? <ErrorMessage testId="compensation-error">{error}</ErrorMessage> : null}

      <ClosingLines prints={w.printsInMinutes} />
    </DialogFrame>
  );
}

// ── On a written consent's draft (PAY-CONSENT) ─────────────────────────────────────────────────

export function ConsentDecisionDialog({
  orgRef,
  consentId,
  committee = false,
  decision,
  answersForm990,
  onClose,
  onSaved,
}: {
  orgRef: string;
  consentId: string;
  /** CBG-YR6: a committee's written consent, whose members sign the block. */
  committee?: boolean;
  decision: BoardCompensationDecisionOut | null;
  answersForm990: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const fields = useDecisionFields(decision);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      // As on a motion: the seat link goes back as it stands (review-187's F4).
      const body = {
        person_name: fields.name.trim(),
        position: fields.position.trim(),
        board_member_id: decision?.board_member_id ?? null,
        form_990_line: answersForm990 ? fields.line : null,
        how_obtained: fields.howObtained.trim() || null,
        reason_outside_range: fields.reason.trim() || null,
      };
      const path = { org_ref: orgRef, consent_id: consentId };
      if (decision === null) {
        await operations.officerAddBoardConsentCompensationDecision({
          path,
          body,
          throwOnError: true,
        });
      } else {
        await operations.officerReplaceBoardConsentCompensationDecision({
          path: { ...path, decision_id: decision.id },
          body,
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(w.saved);
      onSaved();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.title}
      width={600}
      onClose={onClose}
      testId="consent-compensation-dialog"
      footer={
        <SaveButtons
          onCancel={onClose}
          onSave={() => {
            setError(null);
            save.mutate();
          }}
          saving={save.isPending}
          disabled={!fields.complete}
          testId="consent-compensation"
        />
      }
    >
      <WhosePayFields
        name={fields.name}
        position={fields.position}
        line={fields.line}
        answersForm990={answersForm990}
        onName={fields.setName}
        onPosition={fields.setPosition}
        onLine={fields.setLine}
        testId="consent-compensation"
      />
      <div className="flex flex-col gap-10">
        <PartLabel>{committee ? w.committeeReliedOnLabel : w.reliedOnLabel}</PartLabel>
        <TheBoardsWords
          committee={committee}
          howObtained={fields.howObtained}
          reason={fields.reason}
          onHowObtained={fields.setHowObtained}
          onReason={fields.setReason}
          testId="consent-compensation"
        />
      </div>
      {error !== null ? (
        <ErrorMessage testId="consent-compensation-error">{error}</ErrorMessage>
      ) : null}
      <ClosingLines prints={committee ? w.committeePrintsInConsent : w.printsInConsent} />
    </DialogFrame>
  );
}

// ── A decision already in the minute book (an index entry) ─────────────────────────────────────

/** The record kinds an index entry may label, the backend's C12: minutes, resolutions, consents. */
const LABELLED_KINDS = ['minutes', 'resolution', 'written_consent'] as const;
/** The states it may label: what the minute book holds as the record. */
const LABELLED_STATES = new Set(['approved', 'imported']);

/** The records an index entry may label, newest first by the day each is about. */
function useLabellableRecords(orgRef: string, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: ['board-compensation-labellable', orgRef],
    enabled,
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const pages = await Promise.all(
        LABELLED_KINDS.map(async (kind) => {
          const { data } = await operations.officerListBoardRecords({
            path: { org_ref: orgRef },
            query: { record_kind: kind, 'page[limit]': 100 },
            throwOnError: true,
          });
          return extractRecords(data);
        }),
      );
      return pages
        .flat()
        .filter((record) => LABELLED_STATES.has(String(record.state ?? '').toLowerCase()))
        .sort((a, b) => String(b.occurred_on ?? '').localeCompare(String(a.occurred_on ?? '')));
    },
  });
}

function recordChoice(record: OrgBoardRecordOut): string {
  return [record.title, recordKindLabel(record.record_kind), displayDate(record.occurred_on)]
    .filter((part) => typeof part === 'string' && part.length > 0)
    .join(' · ');
}

export function IndexEntryDialog({
  orgRef,
  entry,
  answersForm990,
  onClose,
  onSaved,
}: {
  orgRef: string;
  /** The entry being changed, or null to add one. */
  entry: BoardCompensationDecisionOut | null;
  answersForm990: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const fields = useDecisionFields(entry);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [locator, setLocator] = useState(entry?.decision.record_locator ?? '');
  const [error, setError] = useState<string | null>(null);
  const recordsQuery = useLabellableRecords(orgRef, entry === null);
  const records = recordsQuery.data ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const labels = {
        person_name: fields.name.trim(),
        position: fields.position.trim(),
        form_990_line: answersForm990 ? fields.line : null,
        record_locator: locator.trim() || null,
      };
      if (entry === null) {
        await operations.officerRecordBoardCompensationIndexEntry({
          path: { org_ref: orgRef },
          body: { record_id: recordId ?? '', ...labels },
          throwOnError: true,
        });
      } else {
        await operations.officerUpdateBoardCompensationDecision({
          path: { org_ref: orgRef, decision_id: entry.id },
          body: labels,
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(entry === null ? w.entryAdded : w.saved);
      onSaved();
    },
    onError: (caught) => setError(failure(caught)),
  });

  const nothingToLabel = entry === null && recordsQuery.isSuccess && records.length === 0;

  return (
    <DialogFrame
      title={entry === null ? w.addFromRecords : w.editEntryTitle}
      lead={entry === null ? w.addLead : undefined}
      width={560}
      onClose={onClose}
      testId="compensation-entry-dialog"
      footer={
        <SaveButtons
          onCancel={onClose}
          onSave={() => {
            setError(null);
            save.mutate();
          }}
          saving={save.isPending}
          disabled={!fields.complete || (entry === null && recordId === null)}
          label={entry === null ? w.add : w.save}
          testId="compensation-entry"
        />
      }
    >
      {entry === null ? (
        nothingToLabel ? (
          <p className={SMALL} data-testid="compensation-entry-no-records">
            {w.noRecords}
          </p>
        ) : (
          <Select
            label={w.recordLabel}
            appearance="saas"
            placeholder={w.recordPlaceholder}
            value={recordId}
            onValueChange={setRecordId}
            options={records.map((record) => ({ value: record.id, label: recordChoice(record) }))}
            testId="compensation-entry-record"
          />
        )
      ) : (
        <div className="flex flex-col gap-4">
          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-strong">
            {w.recordLabel}
          </span>
          <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
            {entry.decision.record_title ?? '—'}
          </span>
        </div>
      )}
      <WhosePayFields
        name={fields.name}
        position={fields.position}
        line={fields.line}
        answersForm990={answersForm990}
        onName={fields.setName}
        onPosition={fields.setPosition}
        onLine={fields.setLine}
        testId="compensation-entry"
      />
      <div className="flex flex-col gap-6">
        <TextField
          label={w.whereInRecord}
          value={locator}
          maxLength={300}
          onChange={(event) => setLocator(event.target.value)}
          testId="compensation-entry-locator"
        />
        <span className={SMALL}>{w.locatorHint}</span>
      </div>
      {error !== null ? (
        <ErrorMessage testId="compensation-entry-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

// ── The organization's label, once the details stand ──────────────────────────────────────────

export function AnswerDialog({
  orgRef,
  decision,
  onClose,
  onSaved,
}: {
  orgRef: string;
  decision: BoardCompensationDecisionOut;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const [line, setLine] = useState<Form990Line | null>(form990LineOf(decision.form_990_line));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerUpdateBoardCompensationDecision({
        path: { org_ref: orgRef, decision_id: decision.id },
        body: { form_990_line: line },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(w.answerSaved);
      onSaved();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.answerTitle}
      lead={`${decision.person_name}, ${decision.position}`}
      width={520}
      onClose={onClose}
      testId="compensation-answer-dialog"
      footer={
        <SaveButtons
          onCancel={onClose}
          onSave={() => {
            setError(null);
            save.mutate();
          }}
          saving={save.isPending}
          disabled={line === form990LineOf(decision.form_990_line)}
          testId="compensation-answer"
        />
      }
    >
      <Form990Answer
        value={line}
        onChange={setLine}
        testId="compensation-answer-line"
        showLabel={false}
        allowNeither
      />
      {error !== null ? (
        <ErrorMessage testId="compensation-answer-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}
